import { createContext, useContext, useMemo, useState } from "react";
import { SnackbarProvider } from "notistack";

// Interfaces & Types
import { TChildProperty, TGenericForm, TParentProperty } from "./types";
import {
	ICustomerAddress,
	ILeadInputForm,
	ILeadsContextData,
	ILeadsContextProviderProps,
	ISalesLeadErrors,
	TCustomerType,
} from "../interfaces";

// Constants
import { INTERNET_TYPE } from "@/DataPages/components/CRM/SalesLeads/AddEditPopupDialog/constants";
import { INIT_CUSTOMER, INIT_PROSPECT, INIT_VEHICLE } from "./constants";

// Helpers
import {
	handleErrorAddress,
	handleErrorEmail,
	handleErrorPhones,
	isIndividual,
	isConsentNull,
	isPropertyChildRight,
	isTypeInternet,
} from "./helpers";

// Layouts
import { AUTO_HIDE_DURATION, MAX_SNACK, SnackbarsCustomStyle } from "@/Layouts/components/Snackbars";

// Icons
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import DoneIcon from "@mui/icons-material/Done";

const LeadsContext = createContext<ILeadsContextData | undefined>(undefined);

const useLeadsStore = (store: ILeadsContextData) => {
	return useMemo(() => store, [store]);
};

const LeadsContextProvider = (props: Readonly<ILeadsContextProviderProps>) => {
	const { children = null } = props ?? {};

	const [leadInputForm, setLeadInputForm] = useState<ILeadInputForm>({
		customer: INIT_CUSTOMER,
		prospect: INIT_PROSPECT,
		vehicle: INIT_VEHICLE,
	});

	const [isSavingLead, setIsSavingLead] = useState<boolean>(false);

	const leadErrors = useMemo<Partial<ISalesLeadErrors>>(() => {
		const errors: ISalesLeadErrors = {
			customer: {
				firstInputName: !Boolean(leadInputForm.customer?.firstInputName),
				thirdInputName:
					isIndividual(leadInputForm.customer?.type as TCustomerType) && !Boolean(leadInputForm.customer?.thirdInputName),
				address: handleErrorAddress({
					email: leadInputForm.customer?.email ?? "",
					address: leadInputForm.customer?.address ?? ({} as ICustomerAddress),
					phones: leadInputForm.customer?.phones ?? [],
				}),
				email: handleErrorEmail({
					email: leadInputForm.customer?.email ?? "",
					address: leadInputForm.customer?.address ?? ({} as ICustomerAddress),
					phones: leadInputForm.customer?.phones ?? [],
				}),
				phones: handleErrorPhones({
					email: leadInputForm.customer?.email ?? "",
					address: leadInputForm.customer?.address ?? ({} as ICustomerAddress),
					phones: leadInputForm.customer?.phones ?? [],
				}),
				sharePersonalInfoConsent: isConsentNull(leadInputForm.customer?.sharePersonalInfoConsent),
				usePersonalInfoConsent: isConsentNull(leadInputForm.customer?.usePersonalInfoConsent),
			},
			prospect: {
				category: !Boolean(leadInputForm.prospect?.category),
				type: !Boolean(leadInputForm.prospect?.type),
				providerName: leadInputForm.prospect?.type === INTERNET_TYPE && !Boolean(leadInputForm.prospect?.providerName),
			},
			vehicle: {
				vin: Boolean(leadInputForm.vehicle?.vin),
			},
		};

		return errors;
	}, [leadInputForm]);

	/* Getters Context */
	function getContextValue<T extends unknown, P extends TParentProperty = TParentProperty>(
		parentProperty: P,
		childProperty: TChildProperty<P>
	): T {
		return (leadInputForm as TGenericForm<T>)?.[parentProperty]?.[childProperty];
	}

	function getContextError<P extends TParentProperty = TParentProperty>(parentProperty: P, childProperty: TChildProperty<P>): boolean {
		return (leadErrors as TGenericForm<boolean>)?.[parentProperty]?.[childProperty] ?? false;
	}

	/* Set Context */
	function onChangeContext<T extends unknown, P extends TParentProperty = TParentProperty>(
		parentProperty: P,
		childProperty: TChildProperty<P>,
		value: T
	) {
		setLeadInputForm((prev) => {
			const newValue = {
				...prev,
				[parentProperty]: {
					...prev[parentProperty],
					[childProperty]: value,
				},
			};

			if (isPropertyChildRight({ prospect: parentProperty, type: childProperty }) && !isTypeInternet(value as string)) {
				newValue.prospect.providerName = "";
				newValue.prospect.providerService = "";
			}

			if (isPropertyChildRight({ prospect: parentProperty, category: childProperty })) {
				newValue.prospect.salesPerson = "";
				newValue.prospect.secondarySalesPerson = "";
				newValue.prospect.bdcUser = "";
				newValue.prospect.manager = "";
			}

			return newValue;
		});
	}

	function resetContext() {
		setIsSavingLead(false);
		setLeadInputForm({
			customer: INIT_CUSTOMER,
			prospect: INIT_PROSPECT,
			vehicle: INIT_VEHICLE,
		});
	}

	const store = useLeadsStore({
		leadInputForm,
		leadErrors,
		isSavingLead,
		getContextValue,
		getContextError,
		onChangeContext,
		resetContext,
		setIsSavingLead,
	});

	return (
		<LeadsContext.Provider value={store}>
			<SnackbarProvider
				maxSnack={MAX_SNACK}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				autoHideDuration={AUTO_HIDE_DURATION}
				iconVariant={{
					error: <ErrorOutlineRoundedIcon fontSize="small" className="mr-2" />,
					success: <DoneIcon fontSize="small" className="mr-2" />,
				}}
				Components={{
					error: SnackbarsCustomStyle,
					success: SnackbarsCustomStyle,
				}}
			>
				{children}
			</SnackbarProvider>
		</LeadsContext.Provider>
	);
};

function useLeadsContext() {
	const context = useContext(LeadsContext);
	if (!context) {
		throw new Error("useLeadsContext must be used within a LeadsContextProvider");
	}
	return context;
}

export { LeadsContextProvider, useLeadsContext };
